import { Iterable, List } from 'immutable';
import { useAppSelector } from 'flow/hooks/useRedux';
import {
    ChannelKey,
    ContactKey,
    ExtensionKey,
    QueueKey,
    ReferKey,
    VoicemailKey,
} from 'flow/types/EntityKeys';
import { Profile } from 'flow/types/Profile';
import { TypedMap } from 'flow/types/TypedMap';
import { sortProfilesAfterPlusId } from 'flow/utils/profile-utils';
import { selectOwnExtensionKey } from 'flow/selectors/ExtensionSelectors';

interface ProfilesHook {
    profiles: List<TypedMap<Profile>>;
    activeProfiles: Iterable<number, TypedMap<Profile>>;
    pauseProfiles: Iterable<number, TypedMap<Profile>>;
    sortedProfiles: List<TypedMap<Profile>>;
    sortedActiveProfiles: List<TypedMap<Profile>>;
    getProfile: (profileKey: string) => TypedMap<Profile>;
    hasPermissionToProfile: (key: keyof Profile) => boolean;
}

const useProfiles = (
    // ContactKey shouldn't be here but needs a bigger refactor not to type entityKey to be using ContactKey
    key:
        | ExtensionKey
        | ContactKey
        | ReferKey
        | ChannelKey
        | QueueKey
        | VoicemailKey = null,
): ProfilesHook => {
    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);
    if (!key) {
        key = ownExtensionKey;
    }
    const [entityType] = key.split('-');

    const profiles = useAppSelector((state) => {
        if (entityType === 'channel') {
            return state.channels.getIn(
                ['channels', key as ChannelKey, 'profiles'],
                List(),
            );
        }
        if (entityType === 'queue') {
            return state.pbx.getIn(
                ['queues', key as QueueKey, 'profiles'],
                List(),
            );
        }
        if (entityType === 'refer') {
            return state.pbx.getIn(
                ['refers', key as ReferKey, 'profiles'],
                List(),
            );
        }
        if (entityType === 'extension') {
            return state.profiles.get(key as ExtensionKey, List());
        }

        return List<TypedMap<Profile>>();
    });

    const activeProfiles = profiles.filter(
        (profile) => !profile.get('pauseProfile'),
    );

    const pauseProfiles = profiles.filter((profile) =>
        profile.get('pauseProfile'),
    );

    const getProfile = (profileKey: string) =>
        profiles.find((profile) => profile.get('key') === profileKey);

    const hasPermissionToProfile = (key: keyof Profile) =>
        profiles.some((profile) => profile.has(key));

    return {
        profiles,
        activeProfiles,
        pauseProfiles,
        sortedProfiles: sortProfilesAfterPlusId(profiles),
        sortedActiveProfiles: sortProfilesAfterPlusId(activeProfiles),
        getProfile,
        hasPermissionToProfile,
    };
};

export default useProfiles;
